import {Box, Button, ButtonGroup, Grid, GridItem, IconButton, Input, InputGroup, InputRightElement, ListItem, Menu, MenuButton, MenuItem, MenuList, Text, UnorderedList} from "@chakra-ui/react";
import BeaconAddressInput from "../../form/BeaconAddressInput";
import {BeaconTrigger, MarkerSpot, Spot, Zone} from "../../types";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {KeyboardEvent, useContext, useEffect, useMemo, useState} from "react";
import {BiChevronDown, BiTrash} from "react-icons/bi";
import {useConfirmDialog} from "../../common/ConfirmDialogProvider";
import HelpTooltip from "../../help/HelpTooltip";
import {useOrganizationLibrary} from "../../area/useOrganizationLibrary";

type Props = {
    object: Zone | Spot,
    trigger: BeaconTrigger,
    index: number
    updateCall: (object?: Spot | Zone, newObject?: Spot | Zone) => void
}

const HEX_ONLY = /[^0-9A-Fa-f]/g;
const HEX_OR_DASH = /[^0-9A-Fa-f-]/g;
const FORMATTED_UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const BEACON_UUID_SUFFIX = /^[0-9A-F]{4,12}$/;

const formatFullUuid = (raw: string): string => {
    const hex = raw.replace(HEX_ONLY, "").toUpperCase();
    if (hex.length !== 32) {
        return hex;
    }
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
};

const isBeaconMode = (trigger: BeaconTrigger) => trigger.type !== "iBeacon";

const BeaconTriggerDetails = ({object, trigger, index, updateCall}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const {confirm} = useConfirmDialog();
    const spots = appContext.getSpots();
    const area = appContext.getArea();
    const isSpotObject = (object as Spot)?.type !== undefined;
    const beaconMode = isBeaconMode(trigger);

    const {iBeaconUuids: libraryUuids, addIBeaconUuid} = useOrganizationLibrary(area?.organizationId);

    const [fullUuidInput, setFullUuidInput] = useState<string>((trigger.uuid || "").toUpperCase());
    const [suffixInput, setSuffixInput] = useState<string>((trigger.uuid || "").toUpperCase());

    useEffect(() => {
        setFullUuidInput((trigger.uuid || "").toUpperCase());
        setSuffixInput((trigger.uuid || "").toUpperCase());
    }, [trigger.uuid, trigger.id]);

    const fullUuidHex = fullUuidInput.replace(HEX_ONLY, "");
    const isFullUuidValid = fullUuidInput.length === 0 || fullUuidHex.length === 32;
    const isSuffixValid = suffixInput.length === 0 || (suffixInput.length >= 4 && suffixInput.length <= 12);

    const normalizeMac = (value?: string) => {
        if (!value) {
            return "";
        }
        return value.replace(HEX_ONLY, "").toUpperCase();
    };

    const normalizeIBeaconKey = (candidate: BeaconTrigger): string => {
        if (candidate.type !== "iBeacon") {
            return "";
        }
        const uuidHex = (candidate.uuid || "").replace(HEX_ONLY, "").toUpperCase();
        if (!uuidHex) {
            return "";
        }
        const major = (candidate.majorId || "").trim();
        const minor = (candidate.minorId || "").trim();
        return `${uuidHex}|${major}|${minor}`;
    };

    const otherUsageLabels = useMemo(() => {
        const normalized = normalizeMac(trigger.macAddr);
        if (!normalized) {
            return [];
        }
        const labels = new Set<string>();
        const addLabel = (label?: string) => {
            if (!label) {
                return;
            }
            labels.add(label);
        };
        spots.forEach((spot) => {
            if (!spot?.beaconTriggers?.length) {
                return;
            }
            if (isSpotObject && spot.id === object.id) {
                return;
            }
            const matches = spot.beaconTriggers.some((beaconTrigger) => normalizeMac(beaconTrigger.macAddr) === normalized);
            if (matches) {
                addLabel(`Spot: ${spot.name || spot.id}`);
            }
        });

        if (area?.zones) {
            const zones = area.zones;
            const zoneEntries: {zone: Zone; label: string}[] = [];
            if (zones.entry) {
                zoneEntries.push({zone: zones.entry, label: "Zone: Entry"});
            }
            if (zones.maintenance) {
                zoneEntries.push({zone: zones.maintenance, label: "Zone: Maintenance"});
            }
            if (Array.isArray(zones.ambientZones)) {
                zones.ambientZones.forEach((zone) => {
                    const zoneLabel = zone?.label || zone?.name || zone?.id || "Ambient zone";
                    zoneEntries.push({zone: zone, label: `Ambient Zone: ${zoneLabel}`});
                });
            }
            zoneEntries.forEach(({zone, label}) => {
                if (!zone?.beaconTriggers?.length) {
                    return;
                }
                if (!isSpotObject && zone.id === object.id) {
                    return;
                }
                const matches = zone.beaconTriggers.some((beaconTrigger) => normalizeMac(beaconTrigger.macAddr) === normalized);
                if (matches) {
                    addLabel(label);
                }
            });
        }

        return Array.from(labels.values());
    }, [area, isSpotObject, object.id, spots, trigger.macAddr]);

    const iBeaconOtherUsageLabels = useMemo(() => {
        const normalized = normalizeIBeaconKey(trigger);
        if (!normalized) {
            return [];
        }
        const labels = new Set<string>();
        const addLabel = (label?: string) => {
            if (!label) {
                return;
            }
            labels.add(label);
        };
        spots.forEach((spot) => {
            if (!spot?.beaconTriggers?.length) {
                return;
            }
            if (isSpotObject && spot.id === object.id) {
                return;
            }
            const matches = spot.beaconTriggers.some((beaconTrigger) => normalizeIBeaconKey(beaconTrigger) === normalized);
            if (matches) {
                addLabel(`Spot: ${spot.name || spot.id}`);
            }
        });

        if (area?.zones) {
            const zones = area.zones;
            const zoneEntries: {zone: Zone; label: string}[] = [];
            if (zones.entry) {
                zoneEntries.push({zone: zones.entry, label: "Zone: Entry"});
            }
            if (zones.maintenance) {
                zoneEntries.push({zone: zones.maintenance, label: "Zone: Maintenance"});
            }
            if (Array.isArray(zones.ambientZones)) {
                zones.ambientZones.forEach((zone) => {
                    const zoneLabel = zone?.label || zone?.name || zone?.id || "Ambient zone";
                    zoneEntries.push({zone: zone, label: `Ambient Zone: ${zoneLabel}`});
                });
            }
            zoneEntries.forEach(({zone, label}) => {
                if (!zone?.beaconTriggers?.length) {
                    return;
                }
                if (!isSpotObject && zone.id === object.id) {
                    return;
                }
                const matches = zone.beaconTriggers.some((beaconTrigger) => normalizeIBeaconKey(beaconTrigger) === normalized);
                if (matches) {
                    addLabel(label);
                }
            });
        }

        return Array.from(labels.values());
    }, [area, isSpotObject, object.id, spots, trigger.type, trigger.uuid, trigger.majorId, trigger.minorId]);

    const writeTrigger = (next: BeaconTrigger) => {
        const newTriggers = [...(object.beaconTriggers || [])];
        newTriggers[index] = next;
        updateCall(object, {
            ...object,
            beaconTriggers: newTriggers
        } as MarkerSpot);
    };

    const onAddressChange = (beaconAddr: string) => {
        writeTrigger({...trigger, macAddr: beaconAddr});
    };

    const handleSwitchType = async (nextType: "Beacon" | "iBeacon") => {
        if (nextType === trigger.type) {
            return;
        }
        if (nextType === "Beacon") {
            const currentUuid = (trigger.uuid || "").replace(HEX_ONLY, "");
            if (currentUuid.length > 12) {
                const approved = await confirm({
                    title: "Switch to Beacon mode",
                    body: "Switching to Beacon mode shortens the UUID to its last 12 hex digits. Continue?",
                    confirmText: "Switch",
                    cancelText: "Cancel"
                });
                if (!approved) {
                    return;
                }
                const truncated = currentUuid.slice(-12).toUpperCase();
                writeTrigger({...trigger, type: "Beacon", uuid: truncated});
                return;
            }
            writeTrigger({...trigger, type: "Beacon"});
            return;
        }
        writeTrigger({...trigger, type: "iBeacon"});
    };

    const onSuffixUuidBlur = (value: string) => {
        const trimmed = value.trim().toUpperCase();
        if (trimmed.length > 0 && !BEACON_UUID_SUFFIX.test(trimmed)) {
            return false;
        }
        writeTrigger({...trigger, uuid: trimmed.length > 0 ? trimmed : undefined});
        return true;
    };

    const onFullUuidBlur = (value: string) => {
        const trimmed = value.trim().toUpperCase();
        if (trimmed.length === 0) {
            writeTrigger({...trigger, uuid: undefined});
            return true;
        }
        const hex = trimmed.replace(HEX_ONLY, "");
        if (hex.length !== 32) {
            return false;
        }
        const formatted = formatFullUuid(hex);
        if (!FORMATTED_UUID.test(formatted)) {
            return false;
        }
        writeTrigger({...trigger, uuid: formatted});
        addIBeaconUuid(formatted);
        return true;
    };

    const applyLibraryUuid = (uuid: string) => {
        if (!FORMATTED_UUID.test(uuid)) {
            return;
        }
        setFullUuidInput(uuid);
        writeTrigger({...trigger, uuid: uuid});
    };

    const onMajorMinorBlur = (field: "majorId" | "minorId", value: string) => {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            writeTrigger({...trigger, [field]: ""} as BeaconTrigger);
            return true;
        }
        const parsed = Number(trimmed);
        if (!Number.isInteger(parsed) || parsed < 0 || parsed > 65535) {
            return false;
        }
        writeTrigger({...trigger, [field]: String(parsed)} as BeaconTrigger);
        return true;
    };

    const onNoteBlur = (value: string) => {
        const nextNote = value.trim();
        const currentNote = trigger.note || "";
        if (nextNote === currentNote) {
            return;
        }
        writeTrigger({...trigger, note: nextNote.length > 0 ? nextNote : undefined});
    };

    const enterBlurs = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.currentTarget.blur();
        }
    };

    return <>
        <GridItem marginTop={"16px"} colSpan={4}/>
        <GridItem colStart={1}>
            <Box display={"flex"} alignItems={"center"}>
                <Text fontWeight={"semibold"}>{beaconMode ? "Beacon" : "iBeacon"}</Text>
                {otherUsageLabels.length > 0 && beaconMode ? (
                    <HelpTooltip
                        icon
                        placement={"right"}
                        iconAriaLabel={"Beacon used elsewhere"}
                        content={(
                            <Box>
                                <Text fontWeight={"semibold"} mb={2}>Also used in:</Text>
                                <UnorderedList marginStart={5} spacing={1}>
                                    {otherUsageLabels.map((label) => (
                                        <ListItem key={label}>
                                            <Text fontSize={"sm"}>{label}</Text>
                                        </ListItem>
                                    ))}
                                </UnorderedList>
                            </Box>
                        )}
                    />
                ) : null}
                {iBeaconOtherUsageLabels.length > 0 && !beaconMode ? (
                    <HelpTooltip
                        icon
                        placement={"right"}
                        iconAriaLabel={"iBeacon used elsewhere"}
                        content={(
                            <Box>
                                <Text fontWeight={"semibold"} mb={2}>Also used in:</Text>
                                <UnorderedList marginStart={5} spacing={1}>
                                    {iBeaconOtherUsageLabels.map((label) => (
                                        <ListItem key={label}>
                                            <Text fontSize={"sm"}>{label}</Text>
                                        </ListItem>
                                    ))}
                                </UnorderedList>
                            </Box>
                        )}
                    />
                ) : null}
            </Box>
        </GridItem>

        <GridItem colStart={2}>
            <Grid gridRowGap={"2"} gridColumnGap={"4"}
                  gridTemplateColumns={"max-content max-content max-content max-content"}>
                <Box>Type:</Box>
                <Box gridColumn={"2 / span 3"}>
                    <ButtonGroup size={"xs"} isAttached variant={"outline"}>
                        <Button
                            colorScheme={!beaconMode ? "blue" : undefined}
                            variant={!beaconMode ? "solid" : "outline"}
                            onClick={() => handleSwitchType("iBeacon")}
                            isDisabled={!appContext.isDraft()}
                        >
                            iBeacon
                        </Button>
                        <Button
                            colorScheme={beaconMode ? "blue" : undefined}
                            variant={beaconMode ? "solid" : "outline"}
                            onClick={() => handleSwitchType("Beacon")}
                            isDisabled={!appContext.isDraft()}
                        >
                            Beacon (MAC)
                        </Button>
                    </ButtonGroup>
                </Box>

                {beaconMode ? (
                    <>
                        <Box>Address:</Box>
                        <BeaconAddressInput beaconAddress={trigger.macAddr} index={index}
                                            inputTestId={`beacon-address-input-${trigger.id}`}
                                            onBeaconAddressChange={(addr) => onAddressChange(addr)}
                        />
                        <Box ml={4}>UUID (last 4-12 hex)</Box>
                        <Input size={"sm"} width={"140px"} placeholder={"e.g. A1B2"}
                               value={suffixInput}
                               isInvalid={!isSuffixValid}
                               onChange={(e) => setSuffixInput(e.target.value.replace(HEX_ONLY, "").toUpperCase().slice(0, 12))}
                               onBlur={(e) => {
                                   if (!onSuffixUuidBlur(e.target.value)) {
                                       setSuffixInput((trigger.uuid || "").toUpperCase());
                                   }
                               }}
                               onKeyDown={enterBlurs}
                               disabled={!appContext.isDraft()}
                               title={"Optional: last 4-12 hex digits of the beacon UUID. Leave empty to ignore. Only last digits are used because beacons broadcast truncated UUIDs."}
                        />
                    </>
                ) : (
                    <>
                        <Box>UUID:</Box>
                        <Box gridColumn={"2 / span 3"}>
                            <InputGroup size={"sm"} width={"320px"}>
                                <Input
                                    placeholder={"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
                                    value={fullUuidInput}
                                    isInvalid={!isFullUuidValid}
                                    onChange={(e) => setFullUuidInput(e.target.value.replace(HEX_OR_DASH, "").toUpperCase().slice(0, 36))}
                                    onBlur={(e) => {
                                        if (!onFullUuidBlur(e.target.value)) {
                                            setFullUuidInput((trigger.uuid || "").toUpperCase());
                                        }
                                    }}
                                    onKeyDown={enterBlurs}
                                    disabled={!appContext.isDraft()}
                                    title={"Full iBeacon UUID (32 hex digits, with or without dashes)."}
                                    paddingRight={libraryUuids.length > 0 ? "32px" : undefined}
                                />
                                {libraryUuids.length > 0 ? (
                                    <InputRightElement width={"28px"}>
                                        <Menu>
                                            <MenuButton
                                                as={IconButton}
                                                aria-label={"Pick saved UUID"}
                                                icon={<BiChevronDown/>}
                                                size={"xs"}
                                                variant={"ghost"}
                                                isDisabled={!appContext.isDraft()}
                                            />
                                            <MenuList maxHeight={"240px"} overflowY={"auto"} fontSize={"sm"}>
                                                {libraryUuids.map((uuid) => (
                                                    <MenuItem
                                                        key={uuid}
                                                        fontFamily={"mono"}
                                                        onClick={() => applyLibraryUuid(uuid)}
                                                    >
                                                        {uuid}
                                                    </MenuItem>
                                                ))}
                                            </MenuList>
                                        </Menu>
                                    </InputRightElement>
                                ) : null}
                            </InputGroup>
                        </Box>
                        <Box>Major:</Box>
                        <Input size={"sm"} width={"100px"} placeholder={"0-65535"}
                               defaultValue={trigger.majorId || ""}
                               key={`major-${trigger.id}-${trigger.majorId || ""}`}
                               onBlur={(e) => {
                                   if (!onMajorMinorBlur("majorId", e.target.value)) {
                                       e.target.value = trigger.majorId || "";
                                   }
                               }}
                               onKeyDown={enterBlurs}
                               disabled={!appContext.isDraft()}
                        />
                        <Box ml={4}>Minor:</Box>
                        <Input size={"sm"} width={"100px"} placeholder={"0-65535"}
                               defaultValue={trigger.minorId || ""}
                               key={`minor-${trigger.id}-${trigger.minorId || ""}`}
                               onBlur={(e) => {
                                   if (!onMajorMinorBlur("minorId", e.target.value)) {
                                       e.target.value = trigger.minorId || "";
                                   }
                               }}
                               onKeyDown={enterBlurs}
                               disabled={!appContext.isDraft()}
                        />
                    </>
                )}

                <Box>Notes:</Box>
                <Input
                    size={"sm"}
                    placeholder={"Optional notes"}
                    defaultValue={trigger.note || ""}
                    width={"100%"}
                    gridColumn={"2 / span 3"}
                    key={`note-${trigger.id}-${trigger.note || ""}`}
                    onBlur={(e) => onNoteBlur(e.target.value)}
                    onKeyDown={enterBlurs}
                    disabled={!appContext.isDraft()}
                />
            </Grid>
        </GridItem>

        <GridItem colStart={4}>
            <IconButton variant={"ghost"} aria-label={"Delete"} icon={<BiTrash/>} onClick={async () => {
                if (!object) {
                    return;
                }
                const approved = await confirm({
                    title: "Delete trigger",
                    body: "Are you sure you want to delete this trigger?",
                    confirmText: "Delete",
                    isDestructive: true
                });
                if (!approved) {
                    return;
                }
                const newTriggers = [...object.beaconTriggers ? object.beaconTriggers : []]
                const beaconTriggers = newTriggers.filter((beaconTrig) => beaconTrig.id !== trigger.id);

                updateCall(object, {
                    ...object,
                    beaconTriggers: beaconTriggers
                } as MarkerSpot);
            }}
                        disabled={!appContext.isDraft()}
            />
        </GridItem>

    </>;
}

export default BeaconTriggerDetails
